import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { CurrencyItem } from './CurrencyItem';
import { allPlayerAccounts, Wallet } from '@/api/auth';
import { Currency } from '@/api/common';
import { getCurrentCurrencyData } from '@/helpers/common';
import {
  amountWithCurrencySymbol,
  currencyFlags,
  getCurrencyName,
  mapCurrencyName,
} from '@/helpers/currency';
import { useSwrMutation } from '@/hooks/swr';
import { useCommonStore } from '@/store/commonStore';

import { ScDepositSection } from '@/components/Deposit/styled';

export const CurrenciesAvailableBlock = () => {
  const enableCurrencies = useCommonStore.use.enableCurrencies();
  const wallets = useCommonStore.use.wallets();

  const inactiveCurrenciesList = useMemo(() => {
    const inactiveCurrencies = wallets.map(({ currency }) => currency);
    return enableCurrencies.filter(
      ({ code }) => !inactiveCurrencies.includes(code)
    );
  }, [enableCurrencies, wallets]);

  return (
    <CurrenciesAvailableList inactiveCurrencies={inactiveCurrenciesList} />
  );
};

export const CurrenciesAvailableList: FC<{
  inactiveCurrencies: Currency[];
}> = ({ inactiveCurrencies }) => {
  const { t } = useTranslation('deposits');
  const { enableCurrencies, pendingWallets, isAuthenticated } = useCommonStore(
    useShallow((state) => ({
      enableCurrencies: state.enableCurrencies,
      pendingWallets: state.pendingWallets,
      isAuthenticated: state.isAuthenticated,
    }))
  );

  const { trigger: getAllPlayerAccounts, data: allWallets } = useSwrMutation<
    Wallet[]
  >({
    key: 'createReferralCode',
    fetcher: allPlayerAccounts,
  });

  useEffect(() => {
    if (isAuthenticated) getAllPlayerAccounts();
  }, [isAuthenticated, getAllPlayerAccounts]);

  const optionsWallets = useMemo(
    () =>
      inactiveCurrencies.map(({ code }) => {
        const name = mapCurrencyName[code];
        const balance =
          allWallets?.find(({ currency }) => currency === code)?.amountCents ||
          0;

        const { amount, currency } = getCurrentCurrencyData(
          balance,
          code,
          enableCurrencies
        );

        return {
          name: `${amountWithCurrencySymbol(getCurrencyName(currency), amount)}${
            name ? ` | ${name}` : ''
          }`,
          value: code,
          icon: currencyFlags[code],
        };
      }),
    [inactiveCurrencies, enableCurrencies, allWallets]
  );

  return (
    <ScDepositSection>
      <h3 className="title">{t('editCurrency.title')}</h3>

      <div className="list">
        {optionsWallets.map(({ value, name, icon }) => (
          <CurrencyItem
            key={value}
            currencyCode={value}
            icon={icon}
            label={name}
            disableButton={pendingWallets}
          />
        ))}
      </div>
    </ScDepositSection>
  );
};
